// Eligibility notification queries + promotion/dismiss mutations (#749).
//
// Reads bypass the relation-embed deep-instantiation issue by joining
// student/rank/sport names client-side, same approach used in #746 / #747.
// Writes go through SECURITY DEFINER RPCs (promote_from_notification,
// dismiss_notification, promote_student_manually). The scan RPC
// (run_belt_eligibility_scan from #748) gets a hook too — drives the "Re-scan
// now" button on the promotions page.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';

export interface PendingNotificationRow {
  id: string;
  student_rank_id: string;
  eligible_rank_id: string;
  triggered_by: 'time' | 'attendance' | 'both';
  triggered_at: string;
  status: string;
  // Hydrated client-side:
  student_id: string;
  student_name?: string;
  current_rank_name?: string;
  eligible_rank_name?: string;
  profile_name?: string;
  sport_name?: string;
  achieved_at: string;
  accumulated_time_on_hold_seconds: number;
}

export const notificationKeys = {
  all: ['belt-notifications'] as const,
  pending: (companyId: string | null | undefined) =>
    [...notificationKeys.all, 'pending', companyId ?? 'none'] as const,
};

/**
 * Lists every pending notification scoped to the active company. Hydrates
 * student name, current/eligible rank, profile, sport, and the achieved-at
 * timestamp so the promotion table can render without extra round-trips.
 */
export function usePendingNotifications() {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: notificationKeys.pending(companyId),
    queryFn: async (): Promise<PendingNotificationRow[]> => {
      if (!companyId) return [];

      // 1. Find every student_rank in this company so we can filter
      //    notifications to the active company without an embedded join.
      const { data: ranks, error: ranksErr } = await supabase
        .from('student_ranks')
        .select('id, student_id, sport_id, current_rank_id, achieved_at, accumulated_time_on_hold_seconds')
        .eq('company_id', companyId);
      if (ranksErr) throw ranksErr;
      const rankRows = ranks ?? [];
      if (rankRows.length === 0) return [];

      const rankIds = rankRows.map((r) => r.id);
      const { data: notes, error: notesErr } = await supabase
        .from('belt_eligibility_notifications')
        .select('*')
        .eq('status', 'pending')
        .in('student_rank_id', rankIds)
        .order('triggered_at', { ascending: false });
      if (notesErr) throw notesErr;
      const noteRows = notes ?? [];
      if (noteRows.length === 0) return [];

      // 2. Hydrate display fields in three lookups.
      const studentIds = Array.from(new Set(rankRows.map((r) => r.student_id)));
      const sportIds = Array.from(new Set(rankRows.map((r) => r.sport_id)));
      const beltRankIds = Array.from(
        new Set([
          ...rankRows.map((r) => r.current_rank_id),
          ...noteRows.map((n) => n.eligible_rank_id),
        ])
      );

      const [studentsRes, sportsRes, ranksMetaRes] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('id, first_name, last_name')
          .in('id', studentIds),
        supabase.from('company_sports').select('id, sport_name').in('id', sportIds),
        supabase
          .from('belt_ranks')
          .select('id, name, profile_id')
          .in('id', beltRankIds),
      ]);
      if (studentsRes.error) throw studentsRes.error;
      if (sportsRes.error) throw sportsRes.error;
      if (ranksMetaRes.error) throw ranksMetaRes.error;

      const profileIds = Array.from(
        new Set((ranksMetaRes.data ?? []).map((r) => r.profile_id))
      );
      const profilesRes = await supabase
        .from('belt_profiles')
        .select('id, name')
        .in('id', profileIds);
      if (profilesRes.error) throw profilesRes.error;

      const rankById = new Map(rankRows.map((r) => [r.id, r]));
      const studentById = new Map(
        (studentsRes.data ?? []).map((s) => [s.id, s])
      );
      const sportById = new Map(
        (sportsRes.data ?? []).map((s) => [s.id, s])
      );
      const beltRankById = new Map(
        (ranksMetaRes.data ?? []).map((r) => [r.id, r])
      );
      const profileById = new Map(
        (profilesRes.data ?? []).map((p) => [p.id, p])
      );

      return noteRows.map((n): PendingNotificationRow => {
        const sr = rankById.get(n.student_rank_id)!;
        const student = studentById.get(sr.student_id);
        const sport = sportById.get(sr.sport_id);
        const currentRank = beltRankById.get(sr.current_rank_id);
        const eligibleRank = beltRankById.get(n.eligible_rank_id);
        const profile = currentRank
          ? profileById.get(currentRank.profile_id)
          : undefined;
        return {
          id: n.id,
          student_rank_id: n.student_rank_id,
          eligible_rank_id: n.eligible_rank_id,
          triggered_by: n.triggered_by as PendingNotificationRow['triggered_by'],
          triggered_at: n.triggered_at,
          status: n.status,
          student_id: sr.student_id,
          student_name: student
            ? `${student.first_name ?? ''} ${student.last_name ?? ''}`.trim() || undefined
            : undefined,
          current_rank_name: currentRank?.name,
          eligible_rank_name: eligibleRank?.name,
          profile_name: profile?.name,
          sport_name: sport?.sport_name,
          achieved_at: sr.achieved_at,
          accumulated_time_on_hold_seconds: sr.accumulated_time_on_hold_seconds,
        };
      });
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

// ============================================================================
// Mutations
// ============================================================================

export function usePromoteFromNotification() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();
  return useMutation({
    mutationFn: async (input: { notificationId: string; notes?: string | null }) => {
      const { data, error } = await supabase.rpc('promote_from_notification', {
        p_notification_id: input.notificationId,
        p_notes: input.notes ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.pending(companyId),
      });
      queryClient.invalidateQueries({ queryKey: ['student-ranks'] });
    },
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();
  return useMutation({
    mutationFn: async (input: { notificationId: string; notes?: string | null }) => {
      const { data, error } = await supabase.rpc('dismiss_notification', {
        p_notification_id: input.notificationId,
        p_notes: input.notes ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.pending(companyId),
      });
    },
  });
}

export function usePromoteStudentManually() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();
  return useMutation({
    mutationFn: async (input: {
      studentRankId: string;
      targetRankId: string;
      notes?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('promote_student_manually', {
        p_student_rank_id: input.studentRankId,
        p_target_rank_id: input.targetRankId,
        p_notes: input.notes ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.pending(companyId),
      });
      queryClient.invalidateQueries({ queryKey: ['student-ranks'] });
    },
  });
}

/**
 * Calls the on-demand scan RPC and returns its summary tuple so the UI can
 * tell the user how many notifications fired this run.
 */
export interface ScanResult {
  scanned: number;
  notified_time: number;
  notified_attendance: number;
  notified_both: number;
}

export function useRunEligibilityScan() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();
  return useMutation({
    mutationFn: async (): Promise<ScanResult> => {
      const { data, error } = await supabase.rpc('run_belt_eligibility_scan');
      if (error) throw error;
      const rows = (data ?? []) as ScanResult[];
      return rows[0] ?? { scanned: 0, notified_time: 0, notified_attendance: 0, notified_both: 0 };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.pending(companyId),
      });
    },
  });
}
